import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
	applyLookup,
	findUniqueIds,
	getLookupSourceValue,
	isValidObjectId,
	LookupConfig,
} from "../utils/find_unique_ids_in_list";

function getLookupCacheKey(data: any[], configs: LookupConfig[]): string {
	if (!data?.length || !configs?.length) {
		return "";
	}

	const parts: string[] = [];
	for (const config of configs) {
		const ids = data
			.flatMap((item) => getLookupSourceValue(item, config) || [])
			.filter((id) => isValidObjectId(id));
		const uniqueIds = [...new Set(ids.map(String))].sort();
		parts.push(`${config.sourceKey}:${uniqueIds.join(",")}`);
	}
	return parts.join("|");
}

export function useLookup(
	queryKey: string[],
	data: any[],
	configs: LookupConfig[],
	staleTime?: number
) {
	const cacheKey = useMemo(
		() => getLookupCacheKey(data, configs),
		[data, configs]
	);

	const {
		data: lookupData,
		isLoading,
		isError,
	} = useQuery({
		queryKey: [...queryKey, cacheKey],
		queryFn: () => findUniqueIds(data, configs),
		enabled: !!data?.length,
		staleTime: staleTime ?? 5 * 60 * 1000,
	});

	const dataWithLookup = useMemo(() => {
		if (!lookupData) return data;
		return applyLookup(data, lookupData, configs);
	}, [data, lookupData, configs]);

	return { dataWithLookup, lookupData, isLoading, isError };
}
